import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fmtDate, fmtDateOnly } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, ShieldCheck, Lock, Plus, X, Handshake, Eye, ScrollText,
} from "lucide-react";

export default function WallPage() {
  const qc = useQueryClient();
  const [openCompose, setOpenCompose] = useState(false);
  const [viewId, setViewId] = useState(null);
  const { data } = useQuery({
    queryKey: ["wall"],
    queryFn: () => api.get("/wall").then((r) => r.data),
    refetchInterval: 30000,
  });
  const items = data?.items || [];
  const quotaLeft = data?.quota_left ?? 0;

  return (
    <div data-testid="wall-page" className="p-4 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-mono inline-flex items-center gap-2">
            <Sparkles size={12} /> IDEA_WALL · ELITE_ONLY · MONTHLY
          </div>
          <h1 className="font-mono text-2xl tracking-tight mt-1">The Wall</h1>
          <p className="text-[12px] text-zinc-500 mt-1 max-w-2xl leading-relaxed">
            Encrypted, view-only wall for Elite-tier authors — 1 idea per calendar
            month. Bodies are AES-encrypted at rest; only the author can decrypt.
            <span className="text-amber-300"> All views + engagement requests are
            LOGGED with IP + device</span> for IP-protection first-contact
            evidence. Agreements happen off-platform.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 h-8 px-2 border border-emerald-400/60 bg-emerald-500/5 text-emerald-300 font-mono text-[10px] uppercase tracking-wider">
            <ShieldCheck size={11} /> PROTECTED · LOGGED
          </span>
          <button
            data-testid="wall-new-idea"
            onClick={() => setOpenCompose(true)}
            disabled={quotaLeft <= 0}
            title={quotaLeft <= 0 ? "Monthly quota used or Elite tier required" : "Post an idea (Elite quota)"}
            className="h-8 px-3 inline-flex items-center gap-2 border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 font-mono uppercase text-[10px] tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus size={11} /> NEW_IDEA · {quotaLeft} left this month
          </button>
        </div>
      </header>

      {items.length === 0 ? (
        <div className="border border-[#27272A] bg-[#0E0E0E] p-10 text-center text-[12px] font-mono text-zinc-500 uppercase tracking-wider">
          NO_IDEAS_ON_THE_WALL_THIS_MONTH · be the first
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <IdeaCard key={it.id} it={it} onOpen={() => setViewId(it.id)} />
          ))}
        </div>
      )}

      {openCompose && (
        <ComposeDialog onClose={() => setOpenCompose(false)} onCreated={() => {
          setOpenCompose(false);
          qc.invalidateQueries({ queryKey: ["wall"] });
        }} />
      )}
      {viewId && (
        <IdeaViewer id={viewId} onClose={() => setViewId(null)} />
      )}
    </div>
  );
}

function IdeaCard({ it, onOpen }) {
  return (
    <div
      data-testid={`wall-card-${it.id}`}
      className={`border ${
        it.is_author ? "border-amber-400/60" : "border-[#27272A]"
      } bg-[#0E0E0E] p-3 flex flex-col gap-2`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          {it.month} · {fmtDateOnly(it.created_at)}
        </span>
        {it.is_author ? (
          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-300">
            YOU · {it.status}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-purple-300">
            <Lock size={10} /> ENCRYPTED
          </span>
        )}
      </div>
      <div className="text-[14px] font-mono text-white line-clamp-2">
        {it.title}
      </div>
      <div className="text-[12px] text-zinc-400 line-clamp-3">
        {it.abstract}
      </div>
      {it.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {it.tags.slice(0, 5).map((t) => (
            <span
              key={t}
              className="px-1.5 py-0.5 border border-[#27272A] text-[9px] font-mono text-zinc-400"
            >
              #{t}
            </span>
          ))}
        </div>
      )}
      <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-zinc-500">
        <span>{it.author_email_masked || "anonymous"}</span>
        <span className="tabnum">{it.engagement_count} engagements</span>
      </div>
      <button
        data-testid={`wall-open-${it.id}`}
        onClick={onOpen}
        className="mt-1 h-8 border border-purple-400/60 text-purple-200 hover:bg-purple-400 hover:text-black font-mono uppercase text-[10px] tracking-wider inline-flex items-center justify-center gap-1"
      >
        <Eye size={11} /> {it.is_author ? "OPEN_DETAILS" : "VIEW_AND_ENGAGE"}
      </button>
    </div>
  );
}

function ComposeDialog({ onClose, onCreated }) {
  const [state, setState] = useState({ title: "", abstract: "", body_md: "", tags: "" });
  const submit = useMutation({
    mutationFn: () => api.post("/wall", {
      title: state.title.trim(),
      abstract: state.abstract.trim(),
      body_md: state.body_md,
      tags: state.tags.split(",").map((s) => s.trim()).filter(Boolean),
    }).then((r) => r.data),
    onSuccess: () => {
      toast.success("IDEA_POSTED", { description: "Encrypted at rest · monthly quota consumed" });
      onCreated?.();
    },
    onError: (e) => toast.error("POST_FAILED", {
      description: e?.response?.data?.detail || e?.message || "unknown",
    }),
  });
  const canSubmit = state.title.trim().length >= 3
    && state.abstract.trim().length >= 10
    && state.body_md.trim().length >= 20;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#0A0A0A] border border-purple-400/60 p-5"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-purple-300">
            NEW_IDEA · ENCRYPTED_AT_REST
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white">
            <X size={14} />
          </button>
        </div>
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
          TITLE (public)
        </label>
        <input
          data-testid="wall-compose-title"
          value={state.title}
          maxLength={180}
          onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
          className="w-full h-10 px-2 bg-[#0E0E0E] border border-[#27272A] text-[13px] font-mono text-white focus:border-purple-400 outline-none"
        />
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-3 mb-1">
          ABSTRACT (public · limited write-up, max 400 chars)
        </label>
        <textarea
          data-testid="wall-compose-abstract"
          value={state.abstract}
          maxLength={400}
          rows={3}
          onChange={(e) => setState((s) => ({ ...s, abstract: e.target.value }))}
          className="w-full bg-[#0E0E0E] border border-[#27272A] text-[12px] font-mono text-white p-2 focus:border-purple-400 outline-none"
        />
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-3 mb-1 flex items-center gap-2">
          <Lock size={10} /> FULL_BODY (encrypted · only YOU can decrypt · max 6000 chars)
        </label>
        <textarea
          data-testid="wall-compose-body"
          value={state.body_md}
          maxLength={6000}
          rows={8}
          onChange={(e) => setState((s) => ({ ...s, body_md: e.target.value }))}
          className="w-full bg-[#0E0E0E] border border-purple-500/40 text-[12px] font-mono text-white p-2 focus:border-purple-400 outline-none"
        />
        <label className="block text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-3 mb-1">
          TAGS (comma-separated, max 8)
        </label>
        <input
          data-testid="wall-compose-tags"
          value={state.tags}
          onChange={(e) => setState((s) => ({ ...s, tags: e.target.value }))}
          placeholder="climate, hardware, nsf-2026"
          className="w-full h-9 px-2 bg-[#0E0E0E] border border-[#27272A] text-[11px] font-mono text-white focus:border-purple-400 outline-none"
        />
        <button
          data-testid="wall-compose-submit"
          onClick={() => submit.mutate()}
          disabled={!canSubmit || submit.isPending}
          className="mt-4 w-full h-10 border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
        >
          {submit.isPending ? "ENCRYPTING & POSTING…" : "POST_TO_WALL"}
        </button>
        <p className="text-[10px] font-mono text-zinc-500 mt-2 leading-relaxed">
          You retain full IP · MindStein logs every partner/institution view + engagement
          with IP/device fingerprint as first-contact evidence.
        </p>
      </div>
    </div>
  );
}

function IdeaViewer({ id, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["wall", id],
    queryFn: () => api.get(`/wall/${id}`).then((r) => r.data),
  });
  const [org, setOrg] = useState("");
  const [note, setNote] = useState("");
  const engage = useMutation({
    mutationFn: () => api.post(`/wall/${id}/engage`, { org, note }).then((r) => r.data),
    onSuccess: () => {
      toast.success("ENGAGEMENT_LOGGED", { description: "Your interest was time-stamped for IP protection." });
      qc.invalidateQueries({ queryKey: ["wall", id] });
      qc.invalidateQueries({ queryKey: ["wall"] });
    },
    onError: (e) => toast.error("ENGAGE_FAILED", {
      description: e?.response?.data?.detail || e?.message,
    }),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="wall-viewer"
        className="w-full max-w-2xl h-full overflow-y-auto bg-[#0A0A0A] border-l border-purple-400/40 p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300">
              IDEA_WALL · {data?.month || ""}
            </div>
            <h2 className="font-mono text-xl mt-1 text-white truncate">
              {data?.title || "Loading…"}
            </h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-1">
              {data?.author_email_masked} · {data?.created_at ? fmtDate(data.created_at) : ""}
            </div>
          </div>
          <button
            data-testid="wall-viewer-close"
            onClick={onClose}
            className="h-8 px-3 border border-[#27272A] text-zinc-300 hover:text-white font-mono uppercase text-[10px] tracking-wider"
          >
            CLOSE
          </button>
        </div>
        <div className="mt-4 border-t border-[#161616] pt-4">
          {isLoading ? (
            <div className="text-[12px] font-mono text-zinc-500">LOADING…</div>
          ) : (
            <>
              <div className="text-[13px] font-mono text-zinc-300 whitespace-pre-wrap">
                {data?.abstract}
              </div>
              {data?.is_author && data?.body_md && (
                <>
                  <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300">
                    AUTHOR_ONLY · DECRYPTED
                  </div>
                  <pre className="mt-2 text-[13px] font-mono text-zinc-100 whitespace-pre-wrap leading-relaxed border border-emerald-500/30 bg-emerald-500/5 p-3">
                    {data.body_md}
                  </pre>
                </>
              )}
              {data?.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {data.tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 border border-[#27272A] text-[10px] font-mono text-zinc-400">
                      #{t}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Engagement log for author, or engagement form for viewer */}
        {data?.is_author ? (
          <div className="mt-6 border border-emerald-500/40 bg-emerald-500/5 p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-300 flex items-center gap-2">
              <ScrollText size={11} /> ENGAGEMENT_LOG · {data.engagement_log?.length || 0}
            </div>
            {data.engagement_log?.length ? (
              <ul className="mt-2 space-y-1 max-h-72 overflow-y-auto">
                {data.engagement_log.map((e, i) => (
                  <li
                    key={i}
                    data-testid={`wall-log-${i}`}
                    className="border-b border-[#161616] py-1 grid grid-cols-12 gap-2 text-[10px] font-mono"
                  >
                    <span className="col-span-3 text-zinc-500">{e.at?.slice(0, 19)}</span>
                    <span className="col-span-2 uppercase text-white">{e.kind}</span>
                    <span className="col-span-3 text-zinc-400 truncate">{e.actor_email_masked}</span>
                    <span className="col-span-2 text-zinc-400 truncate">{e.actor_org || "—"}</span>
                    <span className="col-span-2 text-zinc-500 truncate">{e.ip || "—"}</span>
                    {e.note && (
                      <span className="col-span-12 text-zinc-500 italic mt-0.5">↳ {e.note}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-[11px] font-mono text-zinc-500 mt-2">
                No views or engagement requests yet. Every touch gets stamped here.
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 border border-purple-500/40 bg-purple-500/5 p-3 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-purple-300 flex items-center gap-2">
              <Handshake size={11} /> REGISTER_ENGAGEMENT
            </div>
            <p className="text-[11px] font-mono text-zinc-400 leading-relaxed">
              Log a first-contact request. This creates a signed record on the author&apos;s side
              (your masked email + org + IP + timestamp). <span className="text-amber-300">All actual
              agreements happen off-platform.</span>
            </p>
            <input
              data-testid="wall-engage-org"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              placeholder="Your org / firm (optional)"
              className="w-full h-9 px-2 bg-[#0A0A0A] border border-[#27272A] text-[11px] font-mono text-white focus:border-purple-400 outline-none"
            />
            <textarea
              data-testid="wall-engage-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder="Short message (optional, max 400 chars) — this is what the author sees."
              className="w-full p-2 bg-[#0A0A0A] border border-[#27272A] text-[11px] font-mono text-white focus:border-purple-400 outline-none"
            />
            <button
              data-testid="wall-engage-submit"
              onClick={() => engage.mutate()}
              disabled={engage.isPending}
              className="w-full h-9 border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
            >
              {engage.isPending ? "LOGGING…" : "SEND_ENGAGEMENT_REQUEST"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
